import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, Users, Briefcase, Building2, Rocket, GraduationCap, TrendingUp,
  ArrowUpRight, ChevronLeft, RefreshCw, Loader2, ExternalLink, Info,
  UserCheck, FileText, ClipboardList, Handshake, DollarSign, Award, Sparkles,
  Eye, X as CloseIcon,
} from "lucide-react";
import PrivateLayout from "../components/PrivateLayout";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../services/api";

const PERSONAS = [
  { code: "talent",       label: "Talents",              Icon: Users },
  { code: "freelance",    label: "Freelances",           Icon: Briefcase },
  { code: "employeur",    label: "Employeurs & Entreprises", Icon: Building2 },
  { code: "entrepreneur", label: "Entrepreneurs",        Icon: Rocket },
  { code: "universite",   label: "Universités & Formation", Icon: GraduationCap },
  { code: "investisseur", label: "Investisseurs",        Icon: Handshake },
];

const ENTITIES = [
  { v: "any", label: "Tout" },
  { v: "user", label: "Utilisateurs" },
  { v: "offer", label: "Offres" },
  { v: "project", label: "Projets" },
  { v: "formation", label: "Formations" },
  { v: "mission", label: "Missions" },
  { v: "application", label: "Candidatures" },
];

/**
 * AdminEcosystem — /admin/ecosysteme
 * Cockpit transversal read-only pour le Super Admin. Réutilise les données
 * existantes (analytics, users, offers, etc.) — ne duplique jamais les
 * actions opérationnelles (KYC, paiements, modération) : chaque KPI renvoie
 * vers le module dédié existant.
 */
export default function AdminEcosystem() {
  const { user } = useAuth();
  const isAdmin = ["admin", "super-admin"].includes(user?.role);
  const [overview, setOverview] = useState(null);
  const [tab, setTab] = useState("talent");
  const [segment, setSegment] = useState(null);
  const [segmentLoading, setSegmentLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState("");
  const [searchEntity, setSearchEntity] = useState("any");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [relatedUser, setRelatedUser] = useState(null); // { userId, loading, data }

  const openRelated = useCallback(async (userId) => {
    setRelatedUser({ userId, loading: true, data: null });
    try {
      const { data } = await api.get(`/v1/admin/user/${userId}/related`);
      setRelatedUser({ userId, loading: false, data });
    } catch (e) {
      toast.error(formatApiError(e));
      setRelatedUser(null);
    }
  }, []);

  const closeRelated = () => setRelatedUser(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/v1/admin/ecosystem/overview");
      setOverview(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  const loadSegment = useCallback(async (persona) => {
    setSegmentLoading(true);
    try {
      const { data } = await api.get(`/v1/admin/ecosystem/segment/${persona}`);
      setSegment(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSegmentLoading(false); }
  }, []);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);
  useEffect(() => { if (isAdmin) loadSegment(tab); }, [isAdmin, tab, loadSegment]);

  const runSearch = async (e) => {
    e?.preventDefault?.();
    if (!searchQ.trim() || searchQ.trim().length < 2) return;
    setSearching(true);
    try {
      const { data } = await api.get("/v1/admin/search", {
        params: { q: searchQ.trim(), entity: searchEntity, limit: 15 },
      });
      setSearchResults(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSearching(false); }
  };

  if (!isAdmin) {
    return (
      <PrivateLayout title="Écosystème">
        <div className="afr-empty" data-testid="ecosystem-forbidden">
          <div className="afr-empty__title">Accès restreint</div>
        </div>
      </PrivateLayout>
    );
  }

  return (
    <PrivateLayout
      title="🌍 Écosystème AFRICTAL"
      breadcrumbs={[{ to: "/admin", label: "Admin" }, { label: "Écosystème" }]}
      actions={
        <div style={{ display: "flex", gap: 6 }}>
          <button className="afr-btn afr-btn--outline afr-btn--sm" onClick={() => { load(); loadSegment(tab); }} data-testid="ecosystem-refresh">
            <RefreshCw size={12} /> Actualiser
          </button>
          <Link to="/admin" className="afr-btn afr-btn--outline afr-btn--sm"><ChevronLeft size={14} /> Admin</Link>
        </div>
      }
    >
      <div data-testid="admin-ecosystem-page">
      {/* ═══ Barre de recherche globale ═══ */}
      <form onSubmit={runSearch} className="afr-card" style={{ padding: 12, marginBottom: 16, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}
        data-testid="ecosystem-search-form">
        <Search size={16} style={{ color: "var(--afr-primary)" }} />
        <input
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
          placeholder="Rechercher utilisateur, offre, projet, formation, mission…"
          data-testid="ecosystem-search-input"
          style={{ flex: "1 1 300px", padding: "6px 10px", border: "1px solid var(--afr-border,#E2E8F0)", borderRadius: 6, fontSize: "0.88rem" }}
        />
        <select value={searchEntity} onChange={(e) => setSearchEntity(e.target.value)} data-testid="ecosystem-search-entity"
          style={{ padding: "6px 10px", border: "1px solid var(--afr-border,#E2E8F0)", borderRadius: 6, fontSize: "0.86rem" }}>
          {ENTITIES.map((e) => <option key={e.v} value={e.v}>{e.label}</option>)}
        </select>
        <button type="submit" className="afr-btn afr-btn--primary afr-btn--sm" disabled={searching || searchQ.length < 2} data-testid="ecosystem-search-btn">
          {searching ? <Loader2 size={12} className="afr-spin" /> : <Search size={12} />}
          Rechercher
        </button>
      </form>

      {searchResults && <SearchResults data={searchResults} onClear={() => setSearchResults(null)} onOpenUser={openRelated} />}

      {relatedUser && <RelatedDrawer state={relatedUser} onClose={closeRelated} />}

      {/* ═══ KPIs Globaux ═══ */}
      {loading ? (
        <div data-testid="ecosystem-loading">Chargement…</div>
      ) : overview ? (
        <>
          <section data-testid="ecosystem-global-kpis">
            <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 10 }}>
              <Kpi Icon={Users} label="Utilisateurs" value={overview.user_totals.total} accent="#0F2A42"
                trend={overview.user_totals.new_7d} trendLabel="+7j" href="/admin/users" testid="kpi-users-total" />
              <Kpi Icon={TrendingUp} label="Nouveaux (30j)" value={overview.user_totals.new_30d} accent="#009640" testid="kpi-users-30d" />
              <Kpi Icon={FileText} label="Offres" value={overview.opportunities.offers.total} accent="#15803D"
                trend={overview.opportunities.offers.new_7d} trendLabel="+7j" href="/admin/offers" testid="kpi-offers" />
              <Kpi Icon={ClipboardList} label="Candidatures" value={overview.opportunities.applications.total} accent="#B45309" testid="kpi-applications" />
              <Kpi Icon={Briefcase} label="Missions" value={overview.opportunities.missions.total} accent="#0284c7" testid="kpi-missions" />
              <Kpi Icon={Rocket} label="Projets" value={overview.opportunities.projects.total} accent="#7c3aed" testid="kpi-projects" />
              <Kpi Icon={GraduationCap} label="Formations" value={overview.opportunities.formations.total} accent="#0F2A42" testid="kpi-formations" />
              <Kpi Icon={DollarSign} label="Transactions" value={overview.transactions.tx_total} accent="#15803D"
                subvalue={`${overview.transactions.tx_completed} complétées`} href="/admin/payment-core" testid="kpi-tx" />
              <Kpi Icon={Award} label="Certifications" value={overview.outcomes.certifications} accent="#B45309" testid="kpi-certif" />
              <Kpi Icon={Sparkles} label="Talents Elite" value={overview.outcomes.elite_talents} accent="#009640" href="/admin/elite" testid="kpi-elite" />
              <Kpi Icon={UserCheck} label="KYC/KYB approuvés" value={overview.outcomes.kyc_approved} accent="#0284c7" href="/admin/kyc" testid="kpi-kyc" />
            </div>
          </section>

          {/* ═══ Mini-graphique évolution ═══ */}
          <EvolutionChart overview={overview} />

          {/* ═══ Onglets par persona ═══ */}
          <section style={{ marginTop: 20 }} data-testid="ecosystem-persona-tabs">
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 14, borderBottom: "1px solid var(--afr-border,#E2E8F0)" }}>
              {PERSONAS.map((p) => (
                <button
                  key={p.code}
                  onClick={() => setTab(p.code)}
                  className={`afr-btn afr-btn--sm ${tab === p.code ? "afr-btn--primary" : "afr-btn--ghost"}`}
                  data-testid={`persona-tab-${p.code}`}
                  style={{ borderRadius: "6px 6px 0 0" }}
                >
                  <p.Icon size={12} /> {p.label}
                  <span className="afr-badge" style={{ marginLeft: 6, background: "rgba(255,255,255,0.20)" }}>
                    {overview.by_role[p.code]?.total ?? 0}
                  </span>
                </button>
              ))}
            </div>

            {segmentLoading ? <div>Chargement du segment…</div> : segment && (
              <PersonaSegment segment={segment} />
            )}
          </section>

          {/* ═══ Vue transversale ═══ */}
          <CrossFlows overview={overview} />
        </>
      ) : (
        <div className="afr-empty">Impossible de charger les KPIs.</div>
      )}
      </div>
    </PrivateLayout>
  );
}

// ──────────────────────────────────────────────────────────────────────────

function PersonaSegment({ segment }) {
  return (
    <article className="afr-card" data-testid={`persona-segment-${segment.persona}`} style={{ padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: "1rem" }}>{segment.label}</h3>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {segment.navigation.map((n, i) => (
            <Link key={`${segment.persona}-nav-${i}`} to={n.href}
              className="afr-btn afr-btn--outline afr-btn--sm"
              data-testid={`segment-nav-${segment.persona}-${i}`}>
              {n.label} <ExternalLink size={10} />
            </Link>
          ))}
        </div>
      </div>

      <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8 }}>
        {Object.entries(segment.kpis).map(([k, v]) => (
          <div key={k} data-testid={`segment-kpi-${segment.persona}-${k}`}
            style={{ padding: 10, background: "rgba(15,42,66,0.04)", borderRadius: 6 }}>
            <div className="afr-muted" style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: 0.5 }}>{humanKey(k)}</div>
            <div style={{ fontSize: "1.15rem", fontWeight: 700, marginTop: 2 }}>{v}</div>
          </div>
        ))}
      </div>

      {segment.unavailable_note && (
        <div style={{ marginTop: 12, padding: 10, background: "rgba(180,83,9,0.06)", borderRadius: 6, borderLeft: "3px solid #B45309", display: "flex", gap: 8, alignItems: "flex-start" }}
          data-testid={`segment-unavailable-${segment.persona}`}>
          <Info size={14} style={{ color: "#B45309", flexShrink: 0, marginTop: 2 }} />
          <span className="afr-muted" style={{ fontSize: "0.82rem" }}>{segment.unavailable_note}</span>
        </div>
      )}
    </article>
  );
}

function humanKey(k) {
  return ({
    total: "Total",
    new_7d: "Nouveaux (7j)",
    new_30d: "Nouveaux (30j)",
    profile_incomplete: "Profils incomplets",
    banned: "Suspendus",
    applications: "Candidatures (plateforme)",
    kyc_approved: "KYC approuvés",
    elite_labeled: "Elite labellisés",
    reactivation_intents: "Réactivations déclarées",
    services_published: "Services publiés",
    services_active: "Services actifs",
    commissions: "Commissions (plateforme)",
    wallets: "Wallets",
    missions: "Missions (plateforme)",
    offers_total: "Offres publiées",
    offers_active: "Offres approuvées",
    applications_received: "Candidatures reçues",
    kyb_approved: "KYB approuvés",
    projects_total: "Projets créés",
    projects_seeking_funding: "Cherchent financement",
    accompaniments: "Accompagnements IA",
    formations_total: "Formations proposées",
    certifications_delivered: "Certifications délivrées",
    investor_interests: "Manifestations d'intérêt",
  })[k] || k.replace(/_/g, " ");
}

// ──────────────────────────────────────────────────────────────────────────

function SearchResults({ data, onClear, onOpenUser }) {
  const anyResult = data.total > 0;
  return (
    <article className="afr-card" style={{ padding: 12, marginBottom: 16 }} data-testid="ecosystem-search-results">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <strong>Résultats pour « {data.q} » — {data.total} trouvé(s)</strong>
        <button className="afr-btn afr-btn--ghost afr-btn--sm" onClick={onClear} data-testid="ecosystem-search-clear">Fermer</button>
      </div>
      {!anyResult && <div className="afr-muted">Aucun résultat.</div>}
      {Object.entries(data.results || {}).map(([bucket, items]) => (
        items.length > 0 && (
          <div key={bucket} style={{ marginBottom: 10 }}>
            <div className="afr-muted" style={{ fontSize: "0.76rem", textTransform: "uppercase", marginBottom: 4 }}>{bucket}</div>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {items.map((it, i) => (
                <li key={`${bucket}-${it.id || i}`} data-testid={`search-result-${bucket}-${i}`} style={{ marginBottom: 4, fontSize: "0.86rem" }}>
                  <ResultLink bucket={bucket} item={it} onOpenUser={onOpenUser} />
                </li>
              ))}
            </ul>
          </div>
        )
      ))}
    </article>
  );
}

function ResultLink({ bucket, item, onOpenUser }) {
  if (bucket === "users") {
    return (
      <>
        <strong>{item.name || item.email || item.id}</strong>{" "}
        <span className="afr-muted">— {item.role} · {item.email}</span>
        {item.id && (
          <button
            type="button"
            onClick={() => onOpenUser?.(item.id)}
            className="afr-btn afr-btn--ghost afr-btn--sm"
            data-testid={`search-open-related-${item.id}`}
            style={{ marginLeft: 6, padding: "2px 6px" }}
            title="Voir les entités liées (drawer)"
          >
            <Eye size={11} /> Lié
          </button>
        )}
        {item.id && <Link to={`/admin/users?highlight=${item.id}`} style={{ marginLeft: 6 }}>Ouvrir <ArrowUpRight size={10} /></Link>}
      </>
    );
  }
  if (bucket === "offers") {
    return <><strong>{item.title}</strong> <span className="afr-muted">— {item.company || "?"} · {item.moderation_status}</span> {" "}<Link to={`/admin/offers?highlight=${item.id}`}>Ouvrir <ArrowUpRight size={10} /></Link></>;
  }
  return <><strong>{item.title || item.id}</strong> <span className="afr-muted">— {new Date(item.created_at || Date.now()).toLocaleDateString("fr-FR")}</span></>;
}

// ──────────────────────────────────────────────────────────────────────────

function CrossFlows({ overview }) {
  const flows = [
    { id: "t2e", label: "Talent → Candidature → Offre → Employeur",
      counts: [overview.by_role.talent.total, overview.opportunities.applications.total, overview.opportunities.offers.total, overview.by_role.employeur.total],
      hrefs: ["/admin/users?role=talent", "/admin/offers", "/admin/offers", "/admin/users?role=employeur"] },
    { id: "f2p", label: "Freelance → Service → Mission → Client → Paiement → Commission",
      counts: [overview.by_role.freelance.total, overview.opportunities.services.total, overview.opportunities.missions.total, overview.by_role.employeur.total, overview.transactions.tx_total, overview.transactions.commissions_total],
      hrefs: ["/admin/users?role=freelance", "/admin/business-model", "/admin/business-model", "/admin/users?role=employeur", "/admin/payment-core", "/admin/business-model"] },
    { id: "e2t", label: "Entrepreneur → Projet → Besoin → Talent → Accompagnement",
      counts: [overview.by_role.entrepreneur.total, overview.opportunities.projects.total, null, overview.by_role.talent.total, null],
      hrefs: ["/admin/users?role=entrepreneur", "/admin/moderation", null, "/admin/users?role=talent", "/admin/accompagnements"] },
    { id: "u2c", label: "Université → Formation → Talent → Inscription → Certification",
      counts: [overview.by_role.universite.total, overview.opportunities.formations.total, overview.by_role.talent.total, null, overview.outcomes.certifications],
      hrefs: ["/admin/users?role=universite", "/admin/moderation", "/admin/users?role=talent", null, null] },
    { id: "p2i", label: "Projet → Recherche de financement → Investisseur → Mise en relation",
      counts: [overview.opportunities.projects.total, null, overview.by_role.investisseur.total, null],
      hrefs: ["/admin/moderation", null, "/admin/users?role=investisseur", null] },
  ];
  return (
    <section style={{ marginTop: 20 }} data-testid="ecosystem-cross-flows">
      <h3 style={{ margin: "0 0 8px", fontSize: "0.94rem" }}>Vue transversale — Interactions dans l'écosystème</h3>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {flows.map((f) => (
          <article key={f.id} className="afr-card" style={{ padding: 10, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}
            data-testid={`crossflow-${f.id}`}>
            <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap", flex: "1 1 auto", fontSize: "0.84rem" }}>
              {f.label.split(" → ").map((step, i, arr) => {
                const c = f.counts[i];
                const h = f.hrefs[i];
                return (
                  <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                    {h ? (
                      <Link to={h} className="afr-badge" style={{ background: "rgba(21,128,61,0.10)", color: "#15803D", cursor: "pointer" }}
                        data-testid={`crossflow-step-${f.id}-${i}`}>
                        {step} {typeof c === "number" && <strong>· {c}</strong>}
                      </Link>
                    ) : (
                      <span className="afr-badge" style={{ background: "rgba(100,116,139,0.10)", color: "#64748b" }}
                        data-testid={`crossflow-step-${f.id}-${i}`} title="Module non-disponible dans les données actuelles">
                        {step}
                      </span>
                    )}
                    {i < arr.length - 1 && <ArrowUpRight size={10} style={{ transform: "rotate(45deg)", color: "#94a3b8" }} />}
                  </span>
                );
              })}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────

function Kpi({ Icon, label, value, accent, trend, trendLabel, subvalue, href, testid }) {
  const inner = (
    <div className="afr-kpi" data-testid={testid} style={{ borderLeft: `3px solid ${accent}`, cursor: href ? "pointer" : "default", position: "relative" }}>
      <div className="afr-kpi__icon" style={{ background: `color-mix(in oklab, ${accent} 14%, transparent)`, color: accent }}>
        <Icon size={18} />
      </div>
      <span className="afr-kpi__label">{label}</span>
      <span className="afr-kpi__value">{value}</span>
      {typeof trend === "number" && trend > 0 && (
        <span style={{ position: "absolute", top: 8, right: 8, fontSize: "0.7rem", color: "#15803D", fontWeight: 600 }}>
          <TrendingUp size={10} style={{ verticalAlign: "middle" }} /> {trendLabel} +{trend}
        </span>
      )}
      {subvalue && <span className="afr-muted" style={{ fontSize: "0.72rem", marginTop: 2 }}>{subvalue}</span>}
    </div>
  );
  return href ? <Link to={href} style={{ textDecoration: "none", color: "inherit" }}>{inner}</Link> : inner;
}


// ──────────────────────────────────────────────────────────────────────────
// Mini-graphique d'évolution (barres horizontales : 7j vs 30j par persona)

function EvolutionChart({ overview }) {
  const rows = PERSONAS.map((p) => ({
    code: p.code,
    label: p.label,
    Icon: p.Icon,
    total: overview.by_role[p.code]?.total ?? 0,
    new_7d: overview.by_role[p.code]?.new_7d ?? 0,
    new_30d: overview.by_role[p.code]?.new_30d ?? 0,
  }));
  const max = Math.max(1, ...rows.map((r) => r.new_30d));
  return (
    <section className="afr-card" style={{ padding: 14, marginTop: 14 }} data-testid="ecosystem-evolution-chart">
      <h3 style={{ margin: "0 0 10px", fontSize: "0.94rem" }}>Évolution récente — Nouveaux comptes (7 jours vs 30 jours)</h3>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {rows.map((r) => {
          const w30 = Math.round((r.new_30d / max) * 100);
          const w7 = Math.round((r.new_7d / max) * 100);
          return (
            <div key={r.code} data-testid={`evolution-row-${r.code}`} style={{ display: "grid", gridTemplateColumns: "180px 1fr 90px", gap: 10, alignItems: "center" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.85rem" }}>
                <r.Icon size={13} /> {r.label}
              </span>
              <div style={{ position: "relative", height: 18, background: "rgba(15,42,66,0.04)", borderRadius: 4, overflow: "hidden" }}>
                <div style={{ position: "absolute", inset: 0, width: `${w30}%`, background: "rgba(0,150,64,0.20)" }} title={`30j : +${r.new_30d}`} />
                <div style={{ position: "absolute", top: 0, left: 0, bottom: 0, width: `${w7}%`, background: "#009640" }} title={`7j : +${r.new_7d}`} />
              </div>
              <span className="afr-muted" style={{ fontSize: "0.78rem", textAlign: "right" }}>
                +{r.new_7d}/7j · +{r.new_30d}/30j
              </span>
            </div>
          );
        })}
      </div>
      <div className="afr-muted" style={{ marginTop: 8, fontSize: "0.74rem", display: "flex", gap: 12 }}>
        <span><span style={{ display: "inline-block", width: 10, height: 10, background: "#009640", verticalAlign: "middle", marginRight: 4 }} />7 jours</span>
        <span><span style={{ display: "inline-block", width: 10, height: 10, background: "rgba(0,150,64,0.20)", verticalAlign: "middle", marginRight: 4 }} />30 jours</span>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Drawer latéral : entités liées à un utilisateur (read-only)

function RelatedDrawer({ state, onClose }) {
  const { userId, loading, data } = state;
  return (
    <>
      <div
        onClick={onClose}
        data-testid="related-drawer-backdrop"
        style={{ position: "fixed", inset: 0, background: "rgba(15,42,66,0.35)", zIndex: 1200, backdropFilter: "blur(2px)" }}
      />
      <aside
        data-testid="related-drawer"
        style={{
          position: "fixed", top: 0, right: 0, height: "100vh", width: "min(520px, 92vw)",
          background: "#fff", zIndex: 1201, boxShadow: "-8px 0 24px rgba(0,0,0,0.15)",
          display: "flex", flexDirection: "column",
        }}
      >
        <header style={{ padding: 14, borderBottom: "1px solid var(--afr-border,#E2E8F0)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div className="afr-muted" style={{ fontSize: "0.72rem", textTransform: "uppercase" }}>Entités liées</div>
            <div style={{ fontWeight: 700 }}>
              {data?.user?.name || data?.user?.email || userId}
              {data?.user?.role && <span className="afr-muted" style={{ fontWeight: 400, marginLeft: 6, fontSize: "0.82rem" }}>· {data.user.role}</span>}
            </div>
          </div>
          <button onClick={onClose} className="afr-btn afr-btn--ghost afr-btn--sm" data-testid="related-drawer-close">
            <CloseIcon size={14} />
          </button>
        </header>
        <div style={{ flex: 1, overflowY: "auto", padding: 14 }}>
          {loading && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }} data-testid="related-drawer-loading">
              <Loader2 size={14} className="afr-spin" /> Chargement…
            </div>
          )}
          {!loading && data && (
            <>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                <Link to={`/admin/users?highlight=${userId}`} className="afr-btn afr-btn--outline afr-btn--sm" data-testid="related-open-user">
                  Fiche complète <ExternalLink size={10} />
                </Link>
              </div>
              <RelatedGroup title="Candidatures" items={data.applications} render={(x) => (
                <>#{shortId(x.id)} → offre {shortId(x.offer_id)} · <em>{x.status}</em></>
              )} testidPrefix="related-applications" href={(x) => `/admin/offers?highlight=${x.offer_id}`} />
              <RelatedGroup title="Offres publiées" items={data.offers_authored} render={(x) => (
                <><strong>{x.title}</strong> · <em>{x.moderation_status || "?"}</em></>
              )} testidPrefix="related-offers" href={(x) => `/admin/offers?highlight=${x.id}`} />
              <RelatedGroup title="Missions freelance" items={data.missions_freelance} render={(x) => (
                <><strong>{x.title}</strong> · <em>{x.status}</em></>
              )} testidPrefix="related-missions-freelance" />
              <RelatedGroup title="Missions (employeur)" items={data.missions_employer} render={(x) => (
                <><strong>{x.title}</strong> · <em>{x.status}</em></>
              )} testidPrefix="related-missions-employer" />
              <RelatedGroup title="Projets" items={data.projects} render={(x) => (
                <><strong>{x.title}</strong>{x.seeking_funding && <span className="afr-muted"> · cherche financement</span>}</>
              )} testidPrefix="related-projects" />
              <RelatedGroup title="Services freelance" items={data.services} render={(x) => (
                <><strong>{x.title}</strong>{x.is_active === false && <span className="afr-muted"> · inactif</span>}</>
              )} testidPrefix="related-services" />
              <RelatedGroup title="Commissions" items={data.commissions} render={(x) => (
                <>{x.mission_title || "?"} · {x.commission_amount ?? 0} ({x.mode || "?"})</>
              )} testidPrefix="related-commissions" href={() => "/admin/business-model"} />
              <RelatedGroup title="Certifications" items={data.certifications} render={(x) => (
                <><strong>{x.title}</strong></>
              )} testidPrefix="related-certifications" />

              {(!data.counts || Object.values(data.counts).every((n) => n === 0)) && (
                <div className="afr-empty" data-testid="related-drawer-empty">
                  Aucune entité liée à ce compte pour le moment.
                </div>
              )}
            </>
          )}
        </div>
      </aside>
    </>
  );
}

function shortId(id) {
  if (!id) return "—";
  return String(id).slice(0, 8);
}

function RelatedGroup({ title, items, render, href, testidPrefix }) {
  if (!items || items.length === 0) return null;
  return (
    <section style={{ marginBottom: 12 }} data-testid={`${testidPrefix}-group`}>
      <div className="afr-muted" style={{ fontSize: "0.72rem", textTransform: "uppercase", marginBottom: 4 }}>
        {title} · <strong>{items.length}</strong>
      </div>
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {items.map((it, i) => (
          <li key={`${testidPrefix}-${it.id || i}`} data-testid={`${testidPrefix}-item-${i}`} style={{ fontSize: "0.84rem", marginBottom: 3 }}>
            {render(it)}
            {href && <Link to={href(it)} style={{ marginLeft: 6 }}>Ouvrir <ArrowUpRight size={10} /></Link>}
          </li>
        ))}
      </ul>
    </section>
  );
}
